import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import WorkHistoryRoundedIcon from '@mui/icons-material/WorkHistoryRounded';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { alpha, type SxProps, type Theme, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { FeatureFlag, useFeatureFlag } from '@src/config/featureFlags';
// Constants
import { RouterPath } from '@src/constants/router_path';
import { getRouterStateFromLocalStorage } from '@src/modules/local_storage_utils';
// Modules
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { type NavigateFunction, useNavigate } from 'react-router-dom';

function getPillButtonStyles(color: 'primary' | 'secondary', theme: Theme): SxProps<Theme> {
  const paletteColor = theme.palette[color].main;
  const topGlow = alpha(theme.palette.common.white, 0.65);
  const baseGradient = `linear-gradient(180deg, ${alpha(paletteColor, 0.18)} 0%, ${alpha(
    paletteColor,
    0.04
  )} 100%)`;
  const hoverGradient = `linear-gradient(180deg, ${alpha(paletteColor, 0.24)} 0%, ${alpha(
    paletteColor,
    0.08
  )} 100%)`;

  return {
    py: 1.5,
    px: 4,
    borderRadius: '9999px',
    borderWidth: 2,
    borderStyle: 'solid',
    borderColor: alpha(paletteColor, 0.55),
    fontWeight: 700,
    textTransform: 'none',
    color: paletteColor,
    fontSize: { xs: '0.95rem', md: '1rem' },
    minWidth: { xs: 240, md: 280 },
    backgroundColor: alpha(paletteColor, 0.06),
    backgroundImage: baseGradient,
    boxShadow: `inset 0 1px 0 ${topGlow}, 0 8px 18px ${alpha(paletteColor, 0.18)}`,
    transition:
      'transform 0.18s ease, border-color 0.18s ease, background-color 0.18s ease, box-shadow 0.18s ease',
    '& .MuiButton-startIcon, & .MuiButton-endIcon': {
      color: paletteColor,
    },
    '&:hover': {
      borderColor: alpha(paletteColor, 0.7),
      backgroundColor: alpha(paletteColor, 0.1),
      backgroundImage: hoverGradient,
      boxShadow: `inset 0 1px 0 ${topGlow}, 0 10px 22px ${alpha(paletteColor, 0.22)}`,
      transform: 'translateY(-2px)',
    },
    '&:active': {
      transform: 'translateY(0)',
      boxShadow: `inset 0 1px 0 ${topGlow}, 0 6px 16px ${alpha(paletteColor, 0.2)}`,
    },
    '&:focus-visible': {
      boxShadow: `0 0 0 3px ${alpha(paletteColor, 0.25)}, inset 0 1px 0 ${topGlow}`,
    },
  };
}

// Render Function
type ResumeButtonLabels = {
  start: string;
  continue: string;
};

function renderButton(
  path: string,
  nav: NavigateFunction,
  theme: Theme,
  labels: ResumeButtonLabels
) {
  const styles = getPillButtonStyles('primary', theme);
  if (path === '') {
    return (
      <Button
        variant="outlined"
        color="primary"
        size="large"
        fullWidth
        sx={styles}
        onClick={() => {
          // 履歴書のローカルストレージをクリアして経歴書モードに確実に切り替え
          localStorage.removeItem('rirekisho_current_path');
          nav(RouterPath.contact);
        }}
      >
        {labels.start}
      </Button>
    );
  }
  return (
    <Button
      variant="outlined"
      color="primary"
      size="large"
      fullWidth
      sx={styles}
      onClick={() => {
        // 履歴書のローカルストレージをクリアして経歴書モードに確実に切り替え
        localStorage.removeItem('rirekisho_current_path');
        nav(path);
      }}
    >
      {labels.continue}
    </Button>
  );
}

// 履歴書用のボタン描画関数
function renderRirekishoButton(
  path: string,
  nav: NavigateFunction,
  theme: Theme,
  labels: ResumeButtonLabels
) {
  const styles = getPillButtonStyles('secondary', theme);
  if (path === '') {
    return (
      <Button
        variant="outlined"
        color="secondary"
        size="large"
        fullWidth
        sx={styles}
        onClick={() => {
          // 経歴書のローカルストレージをクリアして履歴書モードに確実に切り替え
          localStorage.removeItem('router');
          nav(RouterPath.rirekishoBasicInfo);
        }}
      >
        {labels.start}
      </Button>
    );
  }
  return (
    <Button
      variant="outlined"
      color="secondary"
      size="large"
      fullWidth
      sx={styles}
      onClick={() => {
        // 経歴書のローカルストレージをクリアして履歴書モードに確実に切り替え
        localStorage.removeItem('router');
        nav(path);
      }}
    >
      {labels.continue}
    </Button>
  );
}

function Index(): ReactElement {
  const { t } = useTranslation();
  const theme = useTheme();
  // Variables
  const navigate = useNavigate();
  const pathFromStorage = getRouterStateFromLocalStorage();
  const isRirekishoEnabled = useFeatureFlag(FeatureFlag.USE_RIREKISHO);
  const isResumeEnabled = useFeatureFlag(FeatureFlag.USE_RESUME);
  const showSelectionPrompt = isResumeEnabled && isRirekishoEnabled;

  // 履歴書用のローカルストレージチェック（履歴書用の実装が必要）
  const rirekishoPathFromStorage = localStorage.getItem('rirekisho_current_path') || '';

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: { xs: 4, md: 6 },
        px: { xs: 3, md: 6 },
        py: { xs: 5, md: 7 },
        // background: `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.15)} 0%, ${alpha(theme.palette.secondary.light, 0.2)} 45%, ${theme.palette.background.paper} 95%)`,
        backgroundColor: 'background.paper',
        boxShadow: `0 32px 68px ${alpha(theme.palette.common.black, 0.14)}`,
      }}
    >
      <Box maxWidth={1080} mx="auto">
        <Stack spacing={1.5} textAlign="center" mb={{ xs: 5, md: 6 }}>
          <Typography component="h2" variant="h2">
            {t('home.title')}
          </Typography>
          {showSelectionPrompt && (
            <Typography component="h4" variant="h6">
              {t('home.selectionPrompt')}
            </Typography>
          )}
        </Stack>

        <Stack spacing={4}>
          {isResumeEnabled && (
            <Card
              elevation={0}
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
                background: `linear-gradient(160deg, ${alpha(theme.palette.primary.light, 0.25)} 0%, ${alpha(theme.palette.primary.light, 0.12)} 40%, ${theme.palette.background.paper} 100%)`,
                boxShadow: `0 20px 45px ${alpha(theme.palette.primary.main, 0.12)}`,
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 30px 70px ${alpha(theme.palette.primary.main, 0.2)}`,
                },
              }}
            >
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                alignItems={{ xs: 'flex-start', md: 'center' }}
                justifyContent="space-between"
                spacing={3}
              >
                <Stack spacing={2} flex={1}>
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Avatar
                      variant="rounded"
                      sx={{
                        width: 56,
                        height: 56,
                        backgroundColor: alpha(theme.palette.primary.main, 0.12),
                        color: theme.palette.primary.main,
                        boxShadow: `0 10px 25px ${alpha(theme.palette.primary.main, 0.25)}`,
                      }}
                    >
                      <WorkHistoryRoundedIcon sx={{ fontSize: 28 }} />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" color="text.secondary" fontWeight={600}>
                        {t('home.resumeTagline')}
                      </Typography>
                      <Typography variant="h6" fontWeight={700} color="text.primary">
                        {t('home.resumeHeading')}
                      </Typography>
                    </Box>
                  </Stack>

                  <Typography variant="body2" color="text.primary" sx={{ pl: 0 }}>
                    {t('home.resumeDescription')}
                  </Typography>
                </Stack>

                <Stack spacing={1.5} alignItems={{ xs: 'stretch', md: 'flex-end' }} minWidth={280}>
                  <Link
                    href="/resume_example.pdf"
                    underline="hover"
                    sx={{
                      fontWeight: 600,
                      color: 'primary.main',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      justifyContent: { xs: 'flex-start', md: 'flex-end' },
                    }}
                  >
                    {t('home.exampleLink')}
                    <ArrowForwardIosRoundedIcon sx={{ fontSize: 14 }} />
                  </Link>
                  {renderButton(pathFromStorage, navigate, theme, {
                    start: t('home.resumeStart'),
                    continue: t('home.resumeContinue'),
                  })}
                </Stack>
              </Stack>
            </Card>
          )}

          {isRirekishoEnabled && (
            <Card
              elevation={0}
              sx={{
                p: { xs: 3, md: 4 },
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.secondary.main, 0.18)}`,
                background: `linear-gradient(160deg, ${alpha(theme.palette.secondary.light, 0.28)} 0%, ${alpha(theme.palette.secondary.light, 0.1)} 40%, ${theme.palette.background.paper} 100%)`,
                boxShadow: `0 20px 45px ${alpha(theme.palette.secondary.main, 0.12)}`,
                transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 30px 70px ${alpha(theme.palette.secondary.main, 0.2)}`,
                },
              }}
            >
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                alignItems={{ xs: 'flex-start', md: 'center' }}
                justifyContent="space-between"
                spacing={3}
              >
                <Stack spacing={2} flex={1}>
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Avatar
                      variant="rounded"
                      sx={{
                        width: 56,
                        height: 56,
                        backgroundColor: alpha(theme.palette.secondary.main, 0.12),
                        color: theme.palette.secondary.main,
                        boxShadow: `0 10px 25px ${alpha(theme.palette.secondary.main, 0.25)}`,
                      }}
                    >
                      <DescriptionRoundedIcon sx={{ fontSize: 28 }} />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" color="text.secondary" fontWeight={600}>
                        {t('home.rirekishoTagline')}
                      </Typography>
                      <Typography variant="h6" fontWeight={700} color="text.primary">
                        {t('home.rirekishoHeading')}
                      </Typography>
                    </Box>
                  </Stack>

                  <Typography variant="body2" color="text.primary" sx={{ pl: 0 }}>
                    {t('home.rirekishoDescription')}
                  </Typography>
                </Stack>

                <Stack spacing={1.5} alignItems={{ xs: 'stretch', md: 'flex-end' }} minWidth={280}>
                  <Link
                    href="/rirekisho_example.pdf"
                    underline="hover"
                    sx={{
                      fontWeight: 600,
                      color: 'secondary.main',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      justifyContent: { xs: 'flex-start', md: 'flex-end' },
                    }}
                  >
                    {t('home.rirekishoSample')}
                    <ArrowForwardIosRoundedIcon sx={{ fontSize: 14 }} />
                  </Link>
                  {renderRirekishoButton(rirekishoPathFromStorage, navigate, theme, {
                    start: t('home.rirekishoStart'),
                    continue: t('home.rirekishoContinue'),
                  })}
                </Stack>
              </Stack>
            </Card>
          )}
        </Stack>
      </Box>
    </Paper>
  );
}

export default Index;
